import React from 'react';
import { ReviewGallery } from '../gallery';
import { classes, st } from './gallery-modal-desktop.st.css';
import VotingButtons from '../../review/voting-buttons';
import { GalleryModalProps } from './index';
import UserInfo from '../../user-info/user-info';
import { RatingsInReview } from '../../ratings-in-reviews/rating-in-reviews';
import TextExtended from '../../text-extended/text-extended';
import CollapsibleText from '../../collapsible-text/collapsible-text';
import { useApi } from '../../api-provider/use-api';
import { getReviewState } from '~reviews/common/store/reviews/reviews/reviews-state-selectors';
import { Dialog } from '../../_common/dialog/dialog';
import { useTranslate } from '~reviews/Widget/hooks/use-translate';
import { useConfiguration } from '../../configuration/use-configuration';
import { uniqueId } from 'lodash';
import { MEDIA_MODAL } from '~reviews/common/constants/data-hooks';
import { GALLERY_CONSTS } from '@wix/pro-gallery-old';
import { MediaItem } from '../gallery-media-item';

export const GalleryModalDesktop: React.FC<GalleryModalProps> = ({
  onClose,
  review: { content, id: reviewId, createdDate, author, verified },
  activeIndex,
}) => {
  // We can not have changing props in Modals because of useModal limitations - lets work around it this one time.
  const { votesState, biClickShowMoreBtn } = useApi((state, actions) => ({
    votesState: getReviewState(reviewId, state.reviews)?.votes,
    biClickShowMoreBtn: actions.biClickShowMoreBtn,
  }));
  const { votes: votesSetting } = useConfiguration();
  const t = useTranslate();
  const descriptionId = React.useMemo(() => uniqueId('helpful'), []);
  const options = {
    layoutParams: {
      structure: {
        galleryLayout: GALLERY_CONSTS.layout.THUMBNAIL,
      },
      navigationArrows: {
        position: 'OUTSIDE_GALLERY' as const,
      },
    },
  };

  const isSingleMedia = content.media.length === 1;

  return (
    <Dialog
      onClose={onClose}
      childrenWrapperClassName={classes.dialog}
      className={st(classes.root, { singleMedia: isSingleMedia })}
      aria-label={t('review.media-aria-label')}
      data-hook={MEDIA_MODAL}
    >
      {isSingleMedia ? (
        <MediaItem media={content.media} />
      ) : (
        <ReviewGallery media={content.media} activeIndex={activeIndex} options={options} />
      )}
      <div className={classes.review}>
        <div className={classes.header}>
          <UserInfo className={classes.userInfo} author={author} time={createdDate} />
        </div>
        <RatingsInReview value={content.rating} verified={verified} isBW />
        <div className={classes.title}>
          <TextExtended fontMapping="reviewTitle" colorMapping="black">
            {content.title}
          </TextExtended>
        </div>
        <CollapsibleText
          clamp="auto"
          className={classes.body}
          textClassName={classes.bodyText}
          onClick={(isShowMore) =>
            biClickShowMoreBtn({ isShowMore, origin: 'media_dialog', reviewId })
          }
          text={content.body ?? ''}
          isBW
        />
        {votesState && votesSetting !== 'disabled' && (
          <div className={classes.footer} role="group" aria-labelledby={descriptionId}>
            <TextExtended
              id={descriptionId}
              fontMapping="content"
              colorMapping="black"
              className={classes.wasThisHelpfulText}
            >
              {t('review.was-this-helpful.question')}
            </TextExtended>
            <VotingButtons
              className={classes.voteButtons}
              votesState={votesState}
              reviewId={reviewId}
              reviewsVoting={votesSetting}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};
